/**
 * Policy Type Detection Utilities
 *
 * Provides functions for detecting Conditional Access policy types
 * from API response fields.
 *
 * TDD Cycle 1: Policy Type Detection (Frontend)
 * See: specs/refactor/conditional-access-edit-support.md
 */

import type { ConditionalAccessPolicyResponse } from '../../../types/conditionalAccess';

/**
 * Supported policy types that can be edited.
 */
export type PolicyType =
  | 'session-controls'
  | 'risk-signin'
  | 'risk-user'
  | 'block-legacy-auth'
  | 'passwordless-enforcement'
  | 'app-protection';

/**
 * Detection result type - either a known policy type or 'unknown'.
 */
export type PolicyTypeDetectionResult = PolicyType | 'unknown';

/**
 * Legacy client app types that indicate a block-legacy-auth policy.
 */
const LEGACY_CLIENT_APP_TYPES = ['exchangeActiveSync', 'other'];

/**
 * Grant controls that indicate an app-protection policy.
 */
const APP_PROTECTION_CONTROLS = ['compliantApplication', 'approvedApplication'];

/**
 * Detects the policy type from a Conditional Access policy API response.
 *
 * Detection priority (checked in order):
 * 1. session-controls - sessionControls present and not empty
 * 2. risk-signin - conditions.signInRiskLevels present and not empty
 * 3. risk-user - conditions.userRiskLevels present and not empty
 * 4. block-legacy-auth - conditions.clientAppTypes contains legacy values
 * 5. passwordless-enforcement - grantControls.authenticationStrength present
 * 6. app-protection - grantControls.builtInControls includes compliantApplication or approvedApplication
 * 7. unknown - unrecognized policy type
 *
 * @param policy - The Conditional Access policy response from the API
 * @returns The detected policy type or 'unknown'
 */
export function detectPolicyType(
  policy: ConditionalAccessPolicyResponse
): PolicyTypeDetectionResult {
  // Check sessionControls first
  if (hasNonEmptySessionControls(policy)) {
    return 'session-controls';
  }

  // Check risk levels
  if (hasSignInRiskLevels(policy)) {
    return 'risk-signin';
  }
  if (hasUserRiskLevels(policy)) {
    return 'risk-user';
  }

  // Check legacy auth
  if (hasLegacyClientAppTypes(policy)) {
    return 'block-legacy-auth';
  }

  // Check passwordless
  if (hasAuthenticationStrength(policy)) {
    return 'passwordless-enforcement';
  }

  // Check app protection
  if (hasAppProtectionControls(policy)) {
    return 'app-protection';
  }

  return 'unknown';
}

/**
 * Checks if the policy has non-empty sessionControls.
 */
function hasNonEmptySessionControls(
  policy: ConditionalAccessPolicyResponse
): boolean {
  if (!policy.sessionControls) {
    return false;
  }
  return Object.keys(policy.sessionControls).length > 0;
}

/**
 * Checks if the policy has sign-in risk levels configured.
 */
function hasSignInRiskLevels(policy: ConditionalAccessPolicyResponse): boolean {
  const conditions = policy.conditions as Record<string, unknown>;
  const signInRiskLevels = conditions?.signInRiskLevels;
  return Array.isArray(signInRiskLevels) && signInRiskLevels.length > 0;
}

/**
 * Checks if the policy has user risk levels configured.
 */
function hasUserRiskLevels(policy: ConditionalAccessPolicyResponse): boolean {
  const conditions = policy.conditions as Record<string, unknown>;
  const userRiskLevels = conditions?.userRiskLevels;
  return Array.isArray(userRiskLevels) && userRiskLevels.length > 0;
}

/**
 * Checks if the policy has legacy client app types (block-legacy-auth).
 */
function hasLegacyClientAppTypes(
  policy: ConditionalAccessPolicyResponse
): boolean {
  const conditions = policy.conditions as Record<string, unknown>;
  const clientAppTypes = conditions?.clientAppTypes;
  if (!Array.isArray(clientAppTypes)) {
    return false;
  }
  return clientAppTypes.some((appType) =>
    LEGACY_CLIENT_APP_TYPES.includes(appType as string)
  );
}

/**
 * Checks if the policy has authentication strength (passwordless-enforcement).
 */
function hasAuthenticationStrength(
  policy: ConditionalAccessPolicyResponse
): boolean {
  const grantControls = policy.grantControls as Record<string, unknown>;
  return !!grantControls?.authenticationStrength;
}

/**
 * Checks if the policy has app protection controls.
 */
function hasAppProtectionControls(
  policy: ConditionalAccessPolicyResponse
): boolean {
  const grantControls = policy.grantControls as Record<string, unknown>;
  const builtInControls = grantControls?.builtInControls;
  if (!Array.isArray(builtInControls)) {
    return false;
  }
  return builtInControls.some((control) =>
    APP_PROTECTION_CONTROLS.includes(control as string)
  );
}

/**
 * Transformed policy data for edit mode.
 */
export interface TransformedPolicy {
  id: string;
  displayName: string;
  policy_type: PolicyTypeDetectionResult;
  state: 'enabled' | 'disabled' | 'enabledForReportingButNotEnforced';
  exclude_users: string[];
  // Session controls fields
  sign_in_frequency_hours?: number;
  sign_in_frequency_days?: number;
  persistent_browser_mode?: 'always' | 'never';
  cae_mode?: 'strictEnforcement' | 'disabled' | 'strictLocation';
  // Risk-based policy fields
  risk_levels?: string[];
  risk_action?: 'block' | 'mfa' | 'passwordChange';
}

/**
 * Transforms a Conditional Access policy API response into edit form data.
 *
 * Uses detectPolicyType() to determine the policy type and extracts
 * policy-specific fields based on the detected type.
 *
 * @param policy - The Conditional Access policy response from the API
 * @returns Transformed policy data for the edit form
 */
export function transformPolicyForEdit(
  policy: ConditionalAccessPolicyResponse
): TransformedPolicy {
  const policyType = detectPolicyType(policy);
  const excludeUsers = extractExcludeUsers(policy);

  const baseResult: TransformedPolicy = {
    id: policy.id,
    displayName: policy.displayName,
    policy_type: policyType,
    state: policy.state as 'enabled' | 'disabled' | 'enabledForReportingButNotEnforced',
    exclude_users: excludeUsers,
  };

  // Add type-specific fields
  switch (policyType) {
    case 'session-controls':
      return {
        ...baseResult,
        ...extractSessionControlsFields(policy),
      };
    case 'risk-signin':
      return {
        ...baseResult,
        ...extractRiskSignInFields(policy),
      };
    case 'risk-user':
      return {
        ...baseResult,
        ...extractRiskUserFields(policy),
      };
    default:
      return baseResult;
  }
}

/**
 * Extracts exclude_users from conditions.users.excludeUsers.
 */
function extractExcludeUsers(policy: ConditionalAccessPolicyResponse): string[] {
  const conditions = policy.conditions as Record<string, unknown>;
  const users = conditions?.users as Record<string, unknown> | undefined;
  const excludeUsers = users?.excludeUsers;
  if (Array.isArray(excludeUsers)) {
    return excludeUsers as string[];
  }
  return [];
}

/**
 * Extracts session controls specific fields.
 */
function extractSessionControlsFields(
  policy: ConditionalAccessPolicyResponse
): Partial<TransformedPolicy> {
  const sessionControls = policy.sessionControls;
  if (!sessionControls) {
    return {};
  }

  const result: Partial<TransformedPolicy> = {};

  // Sign-in frequency
  if (sessionControls.signInFrequency) {
    if (sessionControls.signInFrequency.type === 'hours') {
      result.sign_in_frequency_hours = sessionControls.signInFrequency.value;
    } else if (sessionControls.signInFrequency.type === 'days') {
      result.sign_in_frequency_days = sessionControls.signInFrequency.value;
    }
  }

  // Persistent browser
  if (sessionControls.persistentBrowser?.isEnabled) {
    result.persistent_browser_mode = sessionControls.persistentBrowser.mode;
  }

  // Continuous access evaluation
  result.cae_mode =
    sessionControls.continuousAccessEvaluation?.mode || 'disabled';

  return result;
}

/**
 * Extracts risk sign-in specific fields.
 */
function extractRiskSignInFields(
  policy: ConditionalAccessPolicyResponse
): Partial<TransformedPolicy> {
  const conditions = policy.conditions as Record<string, unknown>;
  const signInRiskLevels = conditions?.signInRiskLevels;

  return {
    risk_levels: Array.isArray(signInRiskLevels)
      ? (signInRiskLevels as string[])
      : [],
  };
}

/**
 * Extracts risk user specific fields.
 */
function extractRiskUserFields(
  policy: ConditionalAccessPolicyResponse
): Partial<TransformedPolicy> {
  const conditions = policy.conditions as Record<string, unknown>;
  const userRiskLevels = conditions?.userRiskLevels;
  const grantControls = policy.grantControls as Record<string, unknown>;
  const builtInControls = grantControls?.builtInControls;

  const result: Partial<TransformedPolicy> = {
    risk_levels: Array.isArray(userRiskLevels)
      ? (userRiskLevels as string[])
      : [],
  };

  // Determine risk action from builtInControls
  if (Array.isArray(builtInControls)) {
    if (builtInControls.includes('block')) {
      result.risk_action = 'block';
    } else if (builtInControls.includes('passwordChange')) {
      result.risk_action = 'passwordChange';
    } else if (builtInControls.includes('mfa')) {
      result.risk_action = 'mfa';
    }
  }

  return result;
}

/**
 * Determines whether the Edit menu item should be shown for a policy.
 *
 * Edit is available for all recognized policy types (not 'unknown').
 *
 * @param policy - The Conditional Access policy response from the API
 * @returns true if Edit should be shown, false otherwise
 */
export function shouldShowEditMenuItem(
  policy: ConditionalAccessPolicyResponse
): boolean {
  const policyType = detectPolicyType(policy);
  return policyType !== 'unknown';
}
